import type { MetaDescriptor } from 'react-router';
import type { CanonicalHreflangData, RouteMetaArgs } from '~/types/meta-args';
import type { APCountry } from '~/modules/root';
import type { SupportedCountry } from '@archipro-website/localisation';
import {
    DEFAULT_MARKET,
    getCountryHost,
    getCountryHrefLang,
    SupportedCountries,
} from '@archipro-website/localisation';
import invariant from 'tiny-invariant';
import { getMetaBaseUrl } from '../util/create-meta-base-url';
import { articleAvailableCountries } from '../util/article-indexability';
import { getRouteData } from '~/modules/root/util/get-route-data';

export interface AvailabilityZone {
    Country: {
        Code: APCountry;
    };
}

interface HreflangOption {
    code: SupportedCountry;
    lang: string;
    host: string;
}

/**
 * hreflang alternates for every supported market, derived from the shared
 * country config. Adding a market to `SupportedCountries` automatically wires
 * its canonical host and hreflang here — no per-country edits required.
 */
const HREFLANG_OPTIONS: HreflangOption[] = SupportedCountries.map((code) => ({
    code,
    lang: getCountryHrefLang(code),
    host: getCountryHost(code, { https: true }),
}));

interface GetCanonicalHreflangOptions {
    forceAllRegions?: boolean;
    professionalCountry?: APCountry | string | null;
    noAlternateLinks?: boolean;
    /**
     * Category pages: a page with no availability zones is shown in every
     * market (AvailabilityZoneDomainFilterPolicy), so advertise every market.
     * Location pages are skipped because locations belong to one market.
     */
    allMarketsWhenUnzoned?: boolean;
}

/** Country host config for a market, or undefined for an unknown/unsupported code. */
const hreflangOptionFor = (
    countryCode: APCountry | string | null | undefined,
): HreflangOption | undefined =>
    HREFLANG_OPTIONS.find((option) => option.code === countryCode);

/** @deprecated Use CanonicalHreflangData — kept for mergeMeta generic compatibility. */
export type CanonicalHreflangLoaderArgs = () => CanonicalHreflangData;

/** Meta input accepted from route MetaFunction and RouteMetaArgs SEO helpers. */
export type CanonicalHreflangMetaInput = {
    data?: CanonicalHreflangData | undefined;
    loaderData?: CanonicalHreflangData | undefined;
    location: RouteMetaArgs['location'];
    matches: RouteMetaArgs['matches'];
};

/** Markets to advertise as hreflang alternates for a page. */
const resolveAlternateMarkets = (
    availableCountries: ReadonlyArray<string>,
    pathname: string,
    {
        forceAllRegions,
        noAlternateLinks,
        allMarketsWhenUnzoned,
    }: GetCanonicalHreflangOptions,
): HreflangOption[] => {
    if (noAlternateLinks) return [];
    const allMarkets =
        forceAllRegions ||
        (allMarketsWhenUnzoned &&
            availableCountries.length === 0 &&
            !isLocationPath(pathname));
    if (allMarkets) return HREFLANG_OPTIONS;
    // Otherwise only multi-zone pages get alternates (a single-zone page is
    // canonical to itself).
    if (availableCountries.length <= 1) return [];
    return HREFLANG_OPTIONS.filter(({ code }) =>
        availableCountries.includes(code),
    );
};

/** Normalises react-router MetaArgs for canonical/hreflang helpers (v7 loader-type variance). */
export const toCanonicalHreflangMetaInput = (props: {
    location: RouteMetaArgs['location'];
    matches: readonly unknown[];
    data?: unknown;
    loaderData?: unknown;
}): CanonicalHreflangMetaInput => ({
    location: props.location,
    matches: props.matches as RouteMetaArgs['matches'],
    data: (props.data ?? props.loaderData) as CanonicalHreflangData | undefined,
    loaderData: (props.loaderData ?? props.data) as
        | CanonicalHreflangData
        | undefined,
});

export const getCanonicalHreflang = (
    props: Parameters<typeof toCanonicalHreflangMetaInput>[0],
    options: GetCanonicalHreflangOptions = { forceAllRegions: false },
): MetaDescriptor[] => {
    const normalized = toCanonicalHreflangMetaInput(props);
    const { location, matches, data } = normalized;
    const { professionalCountry } = options;

    const { rootData } = getRouteData(matches);
    invariant(
        rootData?.localisation.country,
        'rootData is required for product category SEO',
    );
    const countryCode: APCountry = rootData.localisation.country;

    // SEO URLs ignore filters; compute the cleaned path once and reuse it for
    // the canonical, alternates and pagination links.
    const cleanPath = removeFilterPathParameters(location.pathname);

    // Handle pagination
    const queryParams = new URLSearchParams(location.search);
    const pageQueryParam = queryParams.get('page');
    const selfUrl = getMetaBaseUrl(normalized) + cleanPath;
    // Dont append page number query param if first page
    const prevPageUrl =
        data?.pagination?.prevPage === 1
            ? selfUrl
            : selfUrl + `?page=${data?.pagination?.prevPage}`;
    const nextPageUrl = selfUrl + `?page=${data?.pagination?.nextPage}`;
    const pagination =
        pageQueryParam && Number(pageQueryParam) !== 1
            ? `?page=${pageQueryParam}`
            : '';

    const canonicalOverride = data?.localisation?.Canonical;
    // Resolve the canonical host from the shared country config. Fall back to
    // the request origin so an unknown/unsupported country can never produce
    // an `undefined` host (which previously broke every US canonical).
    const canonicalHost =
        hreflangOptionFor(professionalCountry || countryCode)?.host ??
        getMetaBaseUrl(normalized);
    const canonicalHref = canonicalHost + cleanPath + pagination;

    // De-duplicate zone country codes so a page available in several regions of
    // one market (e.g. two NZ zones) counts as a single market. Without this,
    // `resolveAlternateMarkets` would treat an effectively single-market article
    // as multi-market and wrongly switch on hreflang alternates. Shared with the
    // article route's indexability/canonical logic via `articleAvailableCountries`.
    const availableCountries = articleAvailableCountries(data?.localisation);

    const alternateLinks = resolveAlternateMarkets(
        availableCountries,
        location.pathname,
        options,
    ).map((country) => {
        return {
            tagName: 'link',
            rel: 'alternate',
            href: removeTrailingSlash(country.host + cleanPath + pagination),
            hrefLang: country.lang,
        };
    });

    // When we expose alternates, advertise an x-default for users whose
    // locale matches no specific market. It points at the origin market host.
    const xDefaultLinks =
        alternateLinks.length > 0
            ? [
                  {
                      tagName: 'link',
                      rel: 'alternate',
                      href: removeTrailingSlash(
                          getCountryHost(DEFAULT_MARKET, { https: true }) +
                              cleanPath +
                              pagination,
                      ),
                      hrefLang: 'x-default',
                  },
              ]
            : [];

    return [
        {
            tagName: 'link',
            rel: 'canonical',
            href: removeTrailingSlash(canonicalOverride ?? canonicalHref),
        },
        ...alternateLinks,
        ...xDefaultLinks,
        ...(data?.pagination && !data?.pagination?.isFirstPage
            ? [
                  {
                      tagName: 'link',
                      rel: 'prev',
                      href: removeTrailingSlash(prevPageUrl),
                  },
              ]
            : []),
        ...(data?.pagination && !data?.pagination?.isLastPage
            ? [
                  {
                      tagName: 'link',
                      rel: 'next',
                      href: removeTrailingSlash(nextPageUrl),
                  },
              ]
            : []),
    ];
};

export const removeTrailingSlash = (str: string): string =>
    str.replace(/\/+$/, '');

/** Location pages: `location-{slug}` segments or a `location_` filter. */
const isLocationPath = (pathname: string): boolean =>
    pathname
        .split('/')
        .some(
            (segment) =>
                segment.startsWith('location-') ||
                segment.startsWith('location_'),
        );

/**
 * SEO Urls should not be impacted by filters.
 * e.g https://archipro.co.nz/products/company_abi-interiors?professionals_size=60
 * should be canonicalised to
 * https://archipro.co.nz/products
 *
 * Remove trailing slash from URL for SEO purposes (avoid wasting crawl budget).
 * URLs with trailing slash are treated as a unique URL, which will flag as a duplicate page
 * when compared to the original page with no trailing slash
 *
 * We can remove all path params that have an underscore (_) these are reserved for filters
 */
const removeFilterPathParameters = (pathname: string): string => {
    const filteredPath = pathname
        .split('/')
        .filter((path) => !path.includes('_'))
        .join('/')
        .replace(/\/$/, '');
    return filteredPath;
};
